import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { cn } from '../../lib/cn'

export interface PeekCarouselHandle {
  goToPrev: () => void
  goToNext: () => void
}

export interface PeekCarouselProps {
  children: ReactNode
  /** Accessible label for the carousel region. */
  ariaLabel?: string
  /** Scroll-track classes. Define the slide width via `--peek-card-w` (e.g. `[--peek-card-w:288px] md:[--peek-card-w:640px]`); each slide uses `w-[var(--peek-card-w)]`. */
  className?: string
  /** Classes applied to every slide wrapper (e.g. `data-[active=false]:opacity-20` for peek dimming). */
  slideClassName?: string
  /** Circular loop: clones the edge slides so a neighbor always peeks on both sides and wrapping is seamless (with >1 slide). */
  loop?: boolean
}

/**
 * Center-snap carousel: active slide centered, neighbors peek. Nav via an
 * imperative handle so controls can live outside (e.g. a header). With `loop`,
 * the edge slides are cloned so it reads as circular (always peeking both
 * sides) and wraps seamlessly. (`Carousel` is the edge-aligned row-scroller
 * alternative.)
 */
export const PeekCarousel = forwardRef<PeekCarouselHandle, PeekCarouselProps>(
  ({ children, ariaLabel, className, slideClassName, loop = false }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
      undefined,
    )
    const isAnimatingRef = useRef(false)
    const prefersReducedMotion = useReducedMotion()

    const realSlides = useMemo(
      () => React.Children.toArray(children),
      [children],
    )
    const realCount = realSlides.length
    const useClones = loop && realCount > 1

    // Circular loop renders [clone(last), ...real, clone(first)] so a card
    // always peeks on both edges and wrapping continues in-direction.
    const domSlides = useMemo(() => {
      if (!useClones) return realSlides
      const clone = (node: ReactNode, key: string): ReactNode =>
        React.isValidElement(node)
          ? React.cloneElement(node as ReactElement, { key })
          : node
      return [
        clone(realSlides[realCount - 1], 'peek-clone-start'),
        ...realSlides,
        clone(realSlides[0], 'peek-clone-end'),
      ]
    }, [realSlides, realCount, useClones])

    const domCount = domSlides.length
    const firstRealDom = useClones ? 1 : 0
    const lastRealDom = useClones ? realCount : realCount - 1

    const [activeDom, setActiveDom] = useState(firstRealDom)
    // Ref mirror so go() reads the current index without depending on it (keeps
    // the imperative handle stable).
    const activeDomRef = useRef(activeDom)
    activeDomRef.current = activeDom

    // The real slide a clone mirrors (identical pixels), else the index itself.
    const realTwinOf = useCallback(
      (domIndex: number) => {
        if (!useClones) return domIndex
        if (domIndex === domCount - 1) return firstRealDom
        if (domIndex === 0) return lastRealDom
        return domIndex
      },
      [useClones, domCount, firstRealDom, lastRealDom],
    )

    const scrollToDom = useCallback((domIndex: number, smooth: boolean) => {
      const container = scrollRef.current
      if (!container) return
      const slide = container.children[domIndex] as HTMLElement | undefined
      if (!slide) return
      const left =
        slide.offsetLeft - (container.clientWidth - slide.offsetWidth) / 2
      container.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' })
    }, [])

    const go = useCallback(
      (dir: 1 | -1) => {
        if (realCount === 0 || isAnimatingRef.current) return
        const { current } = activeDomRef

        const next = useClones
          ? current + dir
          : Math.max(0, Math.min(domCount - 1, current + dir))
        if (next === current) return

        clearTimeout(scrollTimerRef.current)
        setActiveDom(next)
        scrollToDom(next, !prefersReducedMotion)

        if (prefersReducedMotion) {
          // Instant: resolve a clone landing synchronously (no scroll to settle).
          const real = realTwinOf(next)
          if (real !== next) {
            setActiveDom(real)
            scrollToDom(real, false)
          }
          return
        }
        // Smooth: the scroll-settle handler resolves clones + clears the guard.
        isAnimatingRef.current = true
      },
      [
        realCount,
        useClones,
        domCount,
        scrollToDom,
        realTwinOf,
        prefersReducedMotion,
      ],
    )

    const goToPrev = useCallback(() => go(-1), [go])
    const goToNext = useCallback(() => go(1), [go])

    useImperativeHandle(ref, () => ({ goToPrev, goToNext }), [
      goToPrev,
      goToNext,
    ])

    // On scroll settle: sync the active slide to the closest one, reposition off
    // a clone onto its real twin (invisible — identical pixels), clear the guard.
    useEffect(() => {
      const container = scrollRef.current
      if (!container) return undefined

      const handleScroll = () => {
        clearTimeout(scrollTimerRef.current)
        scrollTimerRef.current = setTimeout(() => {
          const el = scrollRef.current
          if (!el) return
          const viewportCenter = el.scrollLeft + el.clientWidth / 2
          let closest = 0
          let best = Infinity
          for (let i = 0; i < el.children.length; i++) {
            const child = el.children[i] as HTMLElement
            const childCenter = child.offsetLeft + child.offsetWidth / 2
            const distance = Math.abs(childCenter - viewportCenter)
            if (distance < best) {
              best = distance
              closest = i
            }
          }
          const real = realTwinOf(closest)
          if (real !== closest) {
            setActiveDom(real)
            scrollToDom(real, false)
          } else {
            setActiveDom(closest)
          }
          isAnimatingRef.current = false
        }, 150)
      }

      container.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        container.removeEventListener('scroll', handleScroll)
        clearTimeout(scrollTimerRef.current)
        // Release the guard: the settle handler that clears it is gone now.
        isAnimatingRef.current = false
      }
    }, [realTwinOf, scrollToDom])

    // Live focus while swiping: the 150ms debounced settle lagged the finger on
    // touch (JAR-3071). Settle stays authoritative on stop (clone→real, guard).
    useEffect(() => {
      const container = scrollRef.current
      if (!container) return undefined
      // jsdom/SSR lack IntersectionObserver — no-op; focus falls back to settle.
      if (typeof IntersectionObserver === 'undefined') return undefined

      const observer = new IntersectionObserver(
        (entries) => {
          if (isAnimatingRef.current) return // arrow-nav destination wins
          // Nearest intersecting card to center, from entry geometry.
          let target: Element | null = null
          let best = Infinity
          for (const entry of entries) {
            if (entry.isIntersecting && entry.rootBounds) {
              const { boundingClientRect: rect, rootBounds } = entry
              const cardCenter = rect.left + rect.width / 2
              const rootCenter = rootBounds.left + rootBounds.width / 2
              const distance = Math.abs(cardCenter - rootCenter)
              if (distance < best) {
                best = distance
                target = entry.target
              }
            }
          }
          if (!target) return
          // Raw index (not realTwinOf) — settle handles the clone→real swap.
          const index = Array.prototype.indexOf.call(container.children, target)
          if (index !== -1 && index !== activeDomRef.current) {
            setActiveDom(index)
          }
        },
        // Narrow center band, thinner than the card gap → ≤1 card intersects.
        { root: container, rootMargin: '0px -49% 0px -49%', threshold: 0 },
      )

      Array.from(container.children).forEach((c) => observer.observe(c))
      return () => observer.disconnect()
    }, [domCount])

    // Center the first real slide on mount / when the slide set changes.
    useEffect(() => {
      setActiveDom(firstRealDom)
      scrollToDom(firstRealDom, false)
    }, [firstRealDom, domCount, scrollToDom])

    return (
      <div
        ref={scrollRef}
        className={cn(
          'flex w-full snap-x snap-mandatory overflow-x-auto [&::-webkit-scrollbar]:hidden',
          className,
        )}
        style={{
          paddingLeft: 'calc(50% - var(--peek-card-w) / 2)',
          paddingRight: 'calc(50% - var(--peek-card-w) / 2)',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        role='region'
        aria-label={ariaLabel}
      >
        {domSlides.map((child, index) => {
          const isClone = useClones && (index === 0 || index === domCount - 1)
          // Clones are decorative twins — hide from AT and remove from tab order.
          const cloneProps: Record<string, unknown> = isClone
            ? { 'aria-hidden': true, inert: true }
            : {}
          return (
            <div
              key={React.isValidElement(child) ? child.key ?? index : index}
              {...cloneProps}
              data-active={index === activeDom}
              className={cn('shrink-0 snap-center', slideClassName)}
            >
              {child}
            </div>
          )
        })}
      </div>
    )
  },
)

PeekCarousel.displayName = 'PeekCarousel'
